import { useEffect, useState } from 'react';
import { Users, Heart } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2D — Trusted Teammates widget.
 * Surfaces: "Trusted by N recurring teammates"
 * Renders an avatar stack + names list. Subtle, friendly, NOT competitive.
 */
function Initial({ name, idx }) {
  const colors = ['#06b6d4', '#a855f7', '#ec4899', '#f59e0b', '#10b981', '#0ea5e9'];
  const bg = colors[idx % colors.length];
  return (
    <div className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-black text-slate-950 ring-2 ring-slate-950"
      style={{ background: bg }}>
      {(name || 'P').slice(0, 1).toUpperCase()}
    </div>
  );
}

export default function ArenaxTrustedTeammates() {
  const [data, setData] = useState({ trusted: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/arenax/player/trusted-teammates').then(r => setData(r.data || { trusted: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  const trusted = data.trusted || [];
  if (trusted.length === 0) return null;

  const headline = trusted.length === 1
    ? `Trusted by ${trusted[0].player_name}`
    : `Trusted by ${trusted.length} recurring teammate${trusted.length !== 1 ? 's' : ''}`;

  return (
    <div className="rounded-2xl border border-pink-500/15 p-5"
      data-testid="arenax-trusted-teammates"
      style={{ background: 'linear-gradient(160deg, rgba(236,72,153,0.06), var(--ax-card-bg-soft))' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="h-4 w-4 text-pink-300" />
          <h3 className="text-base font-bold text-slate-100">{headline}</h3>
        </div>
        <span className="text-[10px] tracking-[0.25em] text-pink-300 font-bold uppercase">YOUR CIRCLE</span>
      </div>
      {/* Avatar stack */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex -space-x-2">
          {trusted.slice(0, 6).map((t, i) => (
            <Initial key={t.player_id} name={t.player_name} idx={i} />
          ))}
        </div>
        {trusted.length > 6 && (
          <span className="text-xs text-slate-400 font-medium">+{trusted.length - 6} more</span>
        )}
      </div>
      {/* Names + chemistry context */}
      <div className="text-sm text-slate-300 leading-relaxed">
        {trusted.slice(0, 3).map(t => (
          <span key={t.player_id} className="inline-flex items-center gap-1 mr-2"
            data-testid={`arenax-trusted-name-${t.player_id}`}>
            <span className="font-bold text-slate-100">{t.player_name}</span>
            {t.mutual_trust && <span className="text-pink-300 text-xs">·  mutual</span>}
          </span>
        ))}
        {trusted.length > 3 && <span className="text-slate-500">and {trusted.length - 3} other{trusted.length - 3 !== 1 ? 's' : ''}</span>}
      </div>
      <p className="text-[11px] text-slate-500 mt-3">
        <Users className="h-3 w-3 inline mr-1" />
        Players you've shared multiple games with who keep saying "Would play again."
      </p>
    </div>
  );
}
